import { useState } from "react";
import { Link } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Cta } from "@/components/site/ui";
import { forgotPassword } from "@/lib/api";
import { IMAGES } from "@/data/content";
import { CheckCircle2 } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!email) return setErr("Please enter your email.");
    setBusy(true);
    try {
      await forgotPassword(email);
      setSent(true);
    } catch {
      setErr("Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "bg-white/5 border-white/15 text-white placeholder:text-white/30 focus-visible:ring-white/40 h-12 rounded-xl";

  return (
    <section className="relative min-h-screen bg-[#1C1A17] text-white overflow-hidden">
      <img src={IMAGES.heroLiving} alt="" className="absolute inset-0 h-full w-full object-cover opacity-15" />
      <div className="absolute inset-0 bg-[#1C1A17]/70" />
      <div className="relative z-10 min-h-screen flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-md rounded-3xl bg-white/[0.04] backdrop-blur-xl border border-white/10 p-8 sm:p-10">
          <Link to="/" className="font-head text-xl font-extrabold tracking-tight">
            SPHERIO LIVING<span className="text-[#1C1A17]">.</span>
          </Link>
          <h1 className="font-head text-3xl font-medium mt-8">Forgot your password?</h1>
          <p className="text-white/55 text-sm mt-2">
            Enter your email and we’ll send you a one-time link to reset it.
          </p>

          {sent ? (
            <div data-testid="forgot-sent" className="mt-8 rounded-2xl border border-[#1C1A17]/30 bg-[#1C1A17]/10 p-6">
              <div className="flex items-center gap-3">
                <CheckCircle2 className="h-6 w-6 text-[#1C1A17]" />
                <div className="font-head text-lg font-medium">Check your inbox</div>
              </div>
              <p className="mt-3 text-white/60 text-sm leading-relaxed">
                If an account exists for <span className="text-white">{email}</span>, we’ve sent a password reset link. It’s valid for 1 hour.
              </p>
              <div className="mt-6">
                <Link to="/login" className="text-[#1C1A17] font-semibold hover:text-white">← Back to sign in</Link>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-6 space-y-4" data-testid="forgot-form">
              {err && (
                <div className="rounded-xl border border-red-400/30 bg-red-500/10 px-4 py-3 text-sm text-red-200">
                  {err}
                </div>
              )}
              <div className="space-y-2">
                <Label className="text-white/70">Email</Label>
                <Input data-testid="forgot-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" className={inputCls} />
              </div>
              <Cta type="submit" onClick={() => {}} data-testid="forgot-submit" variant="light" className="w-full justify-center">
                {busy ? "Sending link..." : "Send reset link"}
              </Cta>
              <div className="text-center text-sm text-white/50">
                Remembered it? <Link to="/login" className="text-white hover:text-[#1C1A17] font-semibold">Sign in</Link>
              </div>
            </form>
          )}

          <div className="mt-8 pt-6 border-t border-white/10 text-center">
            <Link to="/" className="text-white/50 hover:text-white text-sm">← Back to site</Link>
          </div>
        </div>
      </div>
      <Toaster position="top-center" richColors />
    </section>
  );
}
